<script lang="ts">
	import {
		ArrowUpRight,
		Bell,
		Building,
		ChevronRight,
		FileCheck,
		FileText,
		PencilRuler,
	} from "@lucide/svelte";
	import { useSearchParams } from "runed/kit";
	import type { Component } from "svelte";
	import z from "zod";
	import CardSmall from "$lib/components/landing/CardSmall.svelte";
	import Footer from "$lib/components/landing/Footer.svelte";
	import Header from "$lib/components/landing/Header.svelte";
	import { Button } from "$lib/components/ui/button";
	import * as Tabs from "$lib/components/ui/tabs";

	const params = useSearchParams(
		z.object({
			for: z.enum(["manager", "contractor"]).default("manager"),
		}),
	);
</script>

{#snippet item(
	title: string,
	description: string,
	Icon: Component,
	clas?: string,
)}
	<div
		class={[
			"z-10 flex items-center gap-4 rounded-lg border border-gray-200 bg-gray-100 p-4",
			clas,
		]}
	>
		<Icon strokeWidth={1.5} />
		<div class="flex flex-col">
			<span class="font-medium text-black">{title}</span>
			<span class="flex flex-row gap-x-2 text-sm text-gray-500">
				{description}
			</span>
		</div>
	</div>
{/snippet}

{#snippet card(
	hint: string,
	title: string,
	cta: string,
	href: string,
	src: string,
)}
	<div
		class="overflow-hidden rounded-4xl flex flex-col items-center pt-8 md:pt-16 md:px-8 gap-16 bg-white"
	>
		<div class="flex flex-col items-center gap-8 px-8">
			<span class="text-lg text-gray-400">{hint}</span>
			<h3 class="text-pretty text-center">{title}</h3>
			<Button
				{href}
				variant="secondary"
				class="rounded-full h-10 border border-black/4 px-4!"
			>
				{cta}
				<ArrowUpRight />
			</Button>
		</div>
		<div
			class="sm:rounded-2xl sm:-mb-2 py-4 sm:py-8 px-2 sm:px-8 sm:mx-8"
			style="box-shadow: 0 0 50px var(--tw-shadow-color,rgba(0,0,0,.02)),0 0 50px var(--tw-shadow-color,rgba(0,0,0,.04))"
		>
			<img {src} alt={title} class="w-full" />
		</div>
	</div>
{/snippet}

<main class="bg-gray-50 space-y-2">
	<Header />
	<Tabs.Root value={params.for} class="max-w-6xl mx-auto py-6">
		<Tabs.List class="h-auto w-full rounded-xl">
			<Tabs.Trigger
				value="manager"
				onclick={() => (params.for = "manager")}
				class="h-auto md:text-2xl font-normal p-4 sm:p-6 rounded-xl md:gap-4"
			>
				<Building class="size-4 md:size-5" />
				For property managers
			</Tabs.Trigger>
			<Tabs.Trigger
				value="contractor"
				onclick={() => (params.for = "contractor")}
				class="h-auto md:text-2xl font-normal p-4 sm:p-6 rounded-xl md:gap-4"
			>
				<PencilRuler class="size-4 md:size-5" />
				For contractors
			</Tabs.Trigger>
		</Tabs.List>
		<Tabs.Content value="manager" class="space-y-32">
			<section
				class="flex flex-col items-center justify-center gap-6 text-center pt-12"
			>
				<h1>Find the best contractors in your area</h1>
				<p
					class="text-balance text-center text-xl leading-relaxed text-gray-500"
				>
					One platform. All trades. Trusted pros bidding on your job.
				</p>
				<div class="mt-6">
					<Button
						href="/manager"
						class="rounded-full h-12 px-5! bg-black text-white"
					>
						<div>Get Started</div>
						<ChevronRight />
					</Button>
				</div>
			</section>
			<section class="flex flex-col gap-8 lg:flex-row">
				<CardSmall
					title="No calling around"
					description="Take few minutes to post your project, sit back and let the bids roll in"
				>
					{@render item(
						"New bid",
						"Fire Safety Inspection",
						FileText,
						"absolute bottom-8 left-0 right-0 scale-90",
					)}
					{@render item(
						"New bid",
						"Fire Safety Inspection",
						FileText,
						"absolute bottom-4 left-0 right-0 scale-95",
					)}
					{@render item(
						"New bid",
						"Fire Safety Inspection",
						FileText,
						"z-10 mt-8",
					)}
				</CardSmall>
				<CardSmall
					title="Choose the best value"
					description="Award a bid that fits your constraints and requirements"
				>
					<div class="grid grid-cols-3 lg:grid-cols-2 gap-2">
						{#snippet badge(price: string)}
							<div
								class="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-xs text-gray-950"
							>
								<FileText class="size-4" strokeWidth={1.5} />
								{price}
							</div>
						{/snippet}
						{@render badge("$3,850")}
						{@render badge("$9,700")}
						{@render badge("$4,550")}
						{@render badge("$6,900")}
						{@render badge("$7,500")}
						{@render badge("$1,550")}
					</div>
				</CardSmall>
				<CardSmall
					title="Double verification"
					description="We verify all contractors before they can submit bids, and you can double-check their profiles before awarding the bid"
				>
					{@render item(
						"XYZ Fire Protection",
						"Documents verified",
						FileCheck,
					)}
				</CardSmall>
			</section>
			<section class="w-full max-w-6xl flex flex-col gap-24">
				{@render card(
					"Simple registration",
					"Set up your profile in 2 minutes",
					"Set up Profile",
					"/manager",
					"/landing/manager_setup.png",
				)}
				{@render card(
					"Define your project scope, priorities, timelines, and requirements — all in one place",
					"Specify your needs in detail",
					"Post a Project",
					"/manager",
					"/landing/manager_project.png",
				)}
				{@render card(
					"Choose the bid based on the scope, price and contractor's profile",
					"Award the best bid",
					"Request bids",
					"/manager",
					"/landing/manager_bids.png",
				)}
			</section>
		</Tabs.Content>
		<Tabs.Content value="contractor" class="space-y-32">
			<section
				class="flex flex-col items-center justify-center gap-6 text-center pt-12"
			>
				<h1>Get more clients</h1>
				<p
					class="text-balance text-center text-xl leading-relaxed text-gray-500"
				>
					Bid on real projects from verified property managers
				</p>
				<div class="mt-6">
					<Button
						href="/contractor"
						class="rounded-full h-12 px-5! bg-black text-white"
					>
						<div>Get Started</div>
						<ChevronRight />
					</Button>
				</div>
			</section>
			<section class="flex flex-col gap-8 lg:flex-row">
				<CardSmall
					title="Verified clients"
					description="We verify all property managers before they can post projects"
				>
					{@render item(
						"John Doe",
						"Documents verified",
						FileCheck,
						"absolute bottom-8 left-0 right-0 scale-90",
					)}
					{@render item(
						"John Doe",
						"Documents verified",
						FileCheck,
						"absolute bottom-4 left-0 right-0 scale-95",
					)}
					{@render item(
						"John Doe",
						"Documents verified",
						FileCheck,
						"z-10 mt-8",
					)}
				</CardSmall>
				<CardSmall
					title="Relevant projects"
					description="Bid on property service projects across Ontario"
				>
					<div
						class="flex flex-col gap-y-2 rounded-lg border border-gray-200 bg-gray-100 p-4"
					>
						<div class="flex items-center justify-between">
							<span class="text-sm text-black">
								Fire Safety Inspection
							</span>
							<span class="text-sm text-emerald-500">
								Bidding
							</span>
						</div>
						<div
							class="flex items-center justify-between border-t border-gray-200 pt-2 text-sm text-gray-500"
						>
							<span>Your bid</span>
							<span>$4,850 + HST</span>
						</div>
					</div>
				</CardSmall>
				<CardSmall
					title="Instant notificaitons"
					description="Be the first to know when new projects open"
				>
					{@render item(
						"Project opened",
						"Fire Safety Inspection",
						Bell,
						"absolute bottom-8 left-0 right-0 scale-90",
					)}
					{@render item(
						"Project opened",
						"Fire Safety Inspection",
						Bell,
						"absolute bottom-4 left-0 right-0 scale-95",
					)}
					{@render item(
						"Project opened",
						"Fire Safety Inspection",
						Bell,
						"z-10 mt-8",
					)}
				</CardSmall>
			</section>
			<section class="w-full max-w-6xl flex flex-col gap-24">
				{@render card(
					"Easy registration",
					"Set up your profile in 2 minutes",
					"Set up Profile",
					"/contractor",
					"/landing/contractor_setup.png",
				)}
				{@render card(
					"You're in control",
					"You specify the bid scope and price",
					"Bid on Projects",
					"/contractor",
					"/landing/contractor_bid.png",
				)}
				{@render card(
					"Once your bid is awarded, you get manager's contact details",
					"Communicate with the manager directly",
					"Bid on Projects",
					"/contractor",
					"/landing/contractor_awarded.png",
				)}
			</section>
			<section
				class="flex flex-col items-center justify-center gap-6 text-center py-12"
			>
				<p
					class="text-balance text-center text-xl leading-relaxed text-gray-500"
				>
					Free for the first 100 contractors
				</p>
				<h2>$0</h2>
				<div class="mt-6">
					<Button
						href="/contractor"
						class="rounded-full h-12 px-5! bg-black text-white"
					>
						<div>Get Started</div>
						<ChevronRight />
					</Button>
				</div>
			</section>
		</Tabs.Content>
	</Tabs.Root>
	<Footer />
</main>
